import { useState, useEffect, useCallback } from "react";
import { MdCancel, MdCurrencyRupee, MdPets, MdShoppingCart } from "react-icons/md";
import {
  FiEdit3,
  FiCheck,
  FiCalendar,
  FiClock,
  FiHome,
  FiUser,
  FiTrash2,
  FiMinus,
  FiPlus,
} from "react-icons/fi";
import {
  FaCat,
  FaDog,
  FaPlus,
  FaInfoCircle,
  FaClock,
  FaUserTie,
} from "react-icons/fa";
import { HiArrowSmLeft } from "react-icons/hi";
import PetServiceCategoryDrawer from "../Orders/PetServiceCategoryDrawer";
// import SelectAddOnsDrawer from "../../pages/Orders/SelectAddOnsDrawer";
import { parse, isBefore } from "date-fns";
import { httpService } from "../../services/httpService";
import PriceWithDiscount from "../../components/common/PriceWithDiscount";
import Swal from "sweetalert2";

const PetServiceList = ({
  appointments = [],
  products = [],
  mode = "details",
  onEditAppointment = () => {},
  onDeleteAppointment = () => {},
  onDeleteProduct = () => {},
  onDecrementProductQuantity = () => {},
  onIncrementProductQuantity = () => {},
  onStatusUpdate = () => {},
  onOpenAddons = () => {},
  onOpenAddProducts = () => {},
  onOpenOtherCharges = () => {},
  onAppointmentFieldChange = () => {},
  onRoomTypeChange = () => {},
  timeSlots = [],
  employees = [],
  roomTypes = [],
  boardingPriceData = {},
  isCancelledOrCompleted = false,
  isCalculatingPrice = false,
  isOrderCancelled = false,
  clientData = null,
  orderId = null,
  onAppointmentSaved = () => {},
  onRefreshData = () => {},
  onDeleteAppointmentProduct = () => {},
  onDeleteAppointmentAddon = () => {}, // Add this new prop
  onDecrementAddonQuantity = () => {},
  onDeleteAppointmentOtherCharge = () => {}, // Add this new prop
  calculateAppointmentProductsTotal = () => 0,
  autoEditRefId = null,           // <-- new prop: ref_id to trigger edit
  onAutoEditTriggered = () => {}, // <-- optional callback after trigger
  isOrderClosed=false,
  itemCount=0, // <-- new prop: total count of items (appointments + products)
  reloadOrder= () => {},
}) => {
  // State for PetServiceCategoryDrawer
  const [showPetServiceDrawer, setShowPetServiceDrawer] = useState(false);
  const [itemData, setItemData] = useState([]);
  const [drawerEditMode, setDrawerEditMode] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [activeTooltipIndex, setActiveTooltipIndex] = useState(null);

  // State for Products Catalog (per appointment)
  const [catalogOpenForIndex, setCatalogOpenForIndex] = useState(null);
  const [catalogProducts, setCatalogProducts] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState(null);
  const [catalogCart, setCatalogCart] = useState({});

  // State for Add-ons Drawer (per appointment)
  // const [addonsDrawerOpenForIndex, setAddonsDrawerOpenForIndex] =
  //   useState(null);

  const safeFormatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? null : date;
  };

  const calculateNights = (startDate, endDate) => {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getPetIcon = (petType) => {
    let petTypeName = "";
    if (typeof petType === "object" && petType !== null) {
      petTypeName = petType.name?.toLowerCase() || "";
    } else if (typeof petType === "string") {
      petTypeName = petType.toLowerCase();
    }
    if (petTypeName.includes("dog")) {
      return <FaDog className="w-8 h-8 text-teal-600" />;
    } else if (petTypeName.includes("cat")) {
      return <FaCat className="w-8 h-8 text-teal-600" />;
    }
    return <MdPets className="w-8 h-8 text-teal-600" />;
  };

  const getPetTypeName = (petType) => {
    if (!petType) return "";
    if (typeof petType === "object" && petType !== null) {
      return petType.name || "";
    }
    return String(petType);
  };

  // Helper function to get pet type from appointment
  const getAppointmentPetType = (appointment) => {
    if (!appointment.pet_type) return "";

    if (
      typeof appointment.pet_type === "object" &&
      appointment.pet_type !== null
    ) {
      return appointment.pet_type.name?.toLowerCase() || "";
    } else if (typeof appointment.pet_type === "string") {
      return appointment.pet_type.toLowerCase();
    }

    return "";
  };

  // Function to filter room types by pet type
  const getFilteredRoomTypes = (appointment) => {
    if (!appointment || !roomTypes || roomTypes.length === 0) return [];

    const petType = getAppointmentPetType(appointment);
    if (!petType) return roomTypes; // Return all if no pet type specified

    return roomTypes.filter((roomType) => {
      // Check if room has pet_type array
      if (!roomType.pet_type || !Array.isArray(roomType.pet_type)) return false;

      // Check if any pet type in the room matches the appointment's pet type
      return roomType.pet_type.some((pet) => {
        const roomPetName = pet.name?.toLowerCase() || "";
        return roomPetName.includes(petType) || petType.includes(roomPetName);
      });
    });
  };

  const isPastTimeSlot = (slotLabel, selectedDate) => {
    if (!selectedDate) return false;
    try {
      const slotDate = parse(
        `${selectedDate} ${slotLabel}`,
        "yyyy-MM-dd h:mm a",
        new Date(),
      );
      return isBefore(slotDate, new Date());
    } catch {
      return false;
    }
  };

  const todayStr = new Date().toISOString().split("T")[0];

  const closeCatalog = () => {
    setCatalogOpenForIndex(null);
    setCatalogCart({});
  };

  const changeCatalogQty = (id, qty) => {
    setCatalogCart((prev) => ({
      ...prev,
      [id]: Math.max(0, Math.floor(Number(qty) || 0)),
    }));
  };

  const onCatalogAddToCart = (appointmentIndex) => {
    if (!catalogProducts || catalogProducts.length === 0) {
      closeCatalog();
      return;
    }

    const currentAppointmentProducts =
      appointments[appointmentIndex]?.products || [];
    const prev = [...currentAppointmentProducts];
    const map = new Map(prev.map((p) => [p.id, { ...p }]));

    Object.entries(catalogCart).forEach(([id, qty]) => {
      const qtyNum = Number(qty || 0);
      if (qtyNum <= 0) return;

      const product = catalogProducts.find((x) => x.id === id);
      if (!product) return;

      if (map.has(id)) {
        const existing = map.get(id);

        map.set(id, {
          ...existing,
          quantity: Math.min(
            Number(product.maxAvailable ?? Infinity),
            Number(existing.quantity || 0) + qtyNum,
          ),
          maxAvailable: product.maxAvailable ?? existing.maxAvailable,
          price: product.price ?? existing.price,
          name: product.name ?? existing.name,

          // ✅ ADD THESE (IMPORTANT)
          unit_price: product.price ?? existing.unit_price,
          gstAmount: product.gstAmount ?? existing.gstAmount ?? 0,
          amount_without_tax:
            product.beforeGST ?? existing.amount_without_tax ?? 0,
        });
      } else {
        map.set(id, {
          _id: product?._id || null,
          id,
          name: product.name,
          price: product.price,
          quantity: Math.min(Number(product.maxAvailable ?? Infinity), qtyNum),
          maxAvailable: product.maxAvailable,

          // ✅ ADD THESE (IMPORTANT)
          unit_price: product.price,
          gstAmount: product.gstAmount || 0,
          amount_without_tax: product.beforeGST || 0,
        });
      }
    });

    const result = Array.from(map.values());

    const updatedAppointments = [...appointments];
    updatedAppointments[appointmentIndex] = {
      ...updatedAppointments[appointmentIndex],
      products: result,
    };

    if (typeof onAppointmentSaved === "function") {
      onAppointmentSaved(
        updatedAppointments[appointmentIndex],
        appointmentIndex,
      );
    }

    closeCatalog();
  };

  // ===== EDIT APPOINTMENT =====
  // Wrap handleEditClick in useCallback so it's stable for the useEffect
  const handleEditClick = useCallback(async (appointment, index) => {
    try {
      const petTypeName = getPetTypeName(appointment.pet_type);
      const editData = {
        ...appointment,
        pet_id: appointment.pet_id,
        vendor_pet_id: appointment.pet_id,
        service_id: appointment.service_id,
        vendor_client_id: clientData?._id,
        service_category_type_id: appointment.service_category_type_id,
        service_type_name:
          appointment.service_type_name || appointment.service_name,
        service_name: appointment.service_name || appointment.service_type_name,
        room_type_id: appointment.room_type_id,
        room_id: appointment.room_id,
        room_number: appointment.room_number,
        room_name: appointment.room_name,
        technician_id: appointment.technician_id,
        employee_id: appointment.technician_id,
        employee_name: appointment.employee_name || "",
        start_date: appointment.start_date,
        end_date: appointment.end_date,
        start_time: appointment.start_time,
        order_id: orderId,
        item_type: appointment.item_type,
        notes: appointment.notes || "",
        frequency: appointment.frequency || "",
        numberOfTimes: appointment.numberOfTimes || "",
        ref_id: appointment.ref_id || "",
        duration: appointment.duration || "",
        unit_price: appointment.unit_price || 0,
        total_price: appointment.total_price || 0,
        gst: appointment.gst || 0,
        addons: appointment.addons || [],
        staff_id: appointment.technician_id,
        assigned_to: appointment.employee_name,
        _editIndex: index,
        _originalAppointment: { ...appointment },
        pet_type: petTypeName,
      };

      if (appointment.item_type === "dog_walking") {
        editData.service_category_type_id = 1;
        editData.service_type_name = "Dog walking";
      }

      setItemData(editData);
      setEditingIndex(index);
      setDrawerEditMode(true);
      setShowPetServiceDrawer(true);
    } catch (error) {
      console.error("Error preparing appointment for editing:", error);
    }
  }, [clientData?._id, orderId]); // dependencies of the function (only external vars)
  
  const handleCancelClick = useCallback(async (appointment, index) => {
    try {
      let warningMessage = "Do you want to cancel this appointment?";
      if(itemCount === 1) {
        warningMessage = "Do you want to cancel this appointment? This will cancel the entire order.";
      }
      const result = await Swal.fire({
        title: "Are you sure?",
        text: warningMessage,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, cancel it!",
      });
  
      if (result.isConfirmed) {
        try {
          if(itemCount == 1){
            await httpService.post("/vendororder/cancelOrder", {
              order_id: orderId,
            });
        
            Swal.fire({
              title: "Cancelled!",
              text: "Order has been cancelled.",
              icon: "success",
              confirmButtonText: "OK",
            });
            reloadOrder();
          }
          else{
            if(appointment.item_type?.toLowerCase() === "boarding"){
              await httpService.put(
                `/vendorbooking/updateVendorBookingStatus/${
                  appointment.ref_id
                }`,
                {
                  status: "cancelled",
                },
              );
            }
            else
            {
              await httpService.put(
                `/vendorappointment/updateVendorAppointmentStatus/${
                  appointment.ref_id
                }`,
                {
                  status: "cancelled",
                },
              );
            }
    
            Swal.fire({
              title: "Cancelled!",
              text: "Appointment has been cancelled.",
              icon: "success",
              confirmButtonText: "OK",
            });
            reloadOrder();
          }
          onRefreshData();
        } catch (error) {
          console.error("Error cancelling appointment:", error);
          Swal.fire({
            title: "Error",
            text: "Failed to cancel appointment.",
            icon: "error",
            confirmButtonText: "OK",
          });
        }
      }

    } catch (error) {
      console.error("Error Deleting appointment:", error);
    }
  }, [clientData?._id, orderId]); // dependencies of the function (only external vars)
  
  // ===== AUTO EDIT BY REF_ID =====
  useEffect(() => {
    if (!autoEditRefId || appointments.length === 0) return;

    const idx = appointments.findIndex(
      (appt) => appt.ref_id == autoEditRefId
    );

    if (idx !== -1) {
      handleEditClick(appointments[idx], idx);
      // Let the parent know the edit was triggered so it can reset autoEditRefId
      onAutoEditTriggered();
    }
  }, [autoEditRefId, appointments, handleEditClick, onAutoEditTriggered]);


  const handleDrawerClose = () => {
    setShowPetServiceDrawer(false);
    setItemData([]);
    setDrawerEditMode(false);
    setEditingIndex(null);
  };

  const handleSavedAppointment = (data) => {
    onAppointmentSaved(data, editingIndex);
    handleDrawerClose();
  };

  if (appointments.length === 0 && products.length === 0) {
    return null;
  }

  const getBoardingDisplayText = (boarding) => {
    const roomTypeName = boarding.room_name || "Room Type";
    const roomNumber = boarding.room_number || "Not Selected";
    const serviceName = boarding.service_name || "Boarding";

    if (roomTypeName === "Room Type" && roomNumber === "Not Selected") {
      return (
        <>
          {serviceName} -{" "}
          <span className="italic text-sm text-red-500">
            Room details not added
          </span>
        </>
      );
    } else if (roomNumber === "Not Selected") {
      return (
        <>
          {serviceName} - {roomTypeName} -{" "}
          <span className="italic text-sm text-red-500">Room not selected</span>
        </>
      );
    } else {
      return `${serviceName} - ${roomTypeName} - Room ${roomNumber}`;
    }
  };

  return (
    <>
      <div className="space-y-3">
        {/* Appointments Section */}
        {appointments.map((appointment, index) => {
          if (!appointment) return null;

          // ==== original computations (UNCHANGED) ====
          const appointmentDate = safeFormatDate(appointment.start_date);
          const isBoarding = appointment.item_type?.toLowerCase() === "boarding";
          const nights = calculateNights(
            appointment.start_date,
            appointment.end_date,
          );
          const hasAddons = appointment.addons && appointment.addons.length > 0;
          const hasProducts =
            appointment.products && appointment.products.length > 0;
          const productsTotal = calculateAppointmentProductsTotal(
            appointment.products,
          );
          const appointmentStatus = appointment.status;

          const cancelledAppointment = ["cancel", "cancelled"].includes(
            appointmentStatus,
          );
          const appointmentEditEnable = ![
            "completed",
            "closed",
            "cancel",
          ].includes(appointmentStatus);
          const filteredRoomTypes = getFilteredRoomTypes(appointment);

          // ==== financials (UNCHANGED math) ====
          const qty = isBoarding ? nights || 1 : 1;

          const salePricePerUnit = Number(
            appointment.price || appointment.unit_price || 0,
          );

          const activeDiscount = appointment?.discounts?.find(
            (d) => Number(d.is_active) === 1,
          );
          const discountPerUnit = activeDiscount
            ? Number(activeDiscount.discount_amount || 0) ||
              (activeDiscount.type === "percentage"
                ? (salePricePerUnit * Number(activeDiscount.value || 0)) / 100
                : Number(activeDiscount.value || 0))
            : Number(appointment.discount_amount || 0);

          // const mrpPerUnit = salePricePerUnit + discountPerUnit;
          const mrpPerUnit = isBoarding ? appointment.base_total : salePricePerUnit + discountPerUnit;

          const taxPerUnit = Number(
            appointment.discounted_tax ||
              appointment.gst ||
              appointment.tax_amount ||
              0,
          );

          const discountTotal = discountPerUnit * qty;
          const mrpTotal = mrpPerUnit * qty;
          const taxTotal = taxPerUnit * qty;
          const grandTotal = salePricePerUnit * qty;

          const typeLabel = isBoarding
            ? "Boarding"
            : appointment.item_type === "day_care" ||
                appointment.item_type === "daycare"
              ? "Day Care"
              : appointment.item_type === "dog_walking"
                ? "Dog Walking"
                : "Grooming";

          const theme = isBoarding
            ? {
                headerBg: "bg-purple-50",
                headerBorder: "border-purple-100",
                badgeBg: "bg-purple-100",
                badgeText: "text-purple-700",
                divider: "bg-purple-200",
              }
            : {
                headerBg: "bg-orange-50",
                headerBorder: "border-orange-100",
                badgeBg: "bg-orange-100",
                badgeText: "text-orange-700",
                divider: "bg-orange-200",
              };

          return (
            <div
              key={appointment.id || appointment._id || index}
              className="overflow-hidden rounded-lg border border-gray-200 shadow-sm bg-white"
            >
              {/* ===== Header: type badge + actions only ===== */}
              <div
                className={`flex flex-wrap items-center justify-between gap-3 px-3 py-2 ${theme.headerBg} border-b ${theme.headerBorder}`}
              >
                <div className="flex items-center gap-2 min-w-0 flex-wrap">
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${theme.badgeBg} ${theme.badgeText} shrink-0`}
                  >
                    <MdPets className="w-3 h-3" />
                    {typeLabel}
                  </span>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  {mode !== "update" && (
                    <>
                      {!isOrderClosed && !cancelledAppointment && (
                        <>
                          <button
                            onClick={() => onOpenAddProducts(appointment, index)}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-teal-600 text-white text-xs font-medium hover:bg-teal-700 transition"
                          >
                            <FaPlus className="w-2.5 h-2.5" /> Product
                          </button>
                          <button
                            onClick={() => onOpenAddons(appointment, index)}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-green-600 text-white text-xs font-medium hover:bg-green-700 transition"
                          >
                            <FaPlus className="w-2.5 h-2.5" /> Add-on
                          </button>
                          <button
                            onClick={() => onOpenOtherCharges(appointment, index)}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-purple-600 text-white text-xs font-medium hover:bg-purple-700 transition"
                          >
                            <FaPlus className="w-2.5 h-2.5" /> Other Charge
                          </button>
                          <span className={`w-px h-5 ${theme.divider} mx-1`} />
                        </>
                      )}
                    </>
                  )}

                  {mode !== "update" && appointmentEditEnable && (
                    <>
                      {!isOrderClosed && !cancelledAppointment && (
                        <>
                          <button
                            onClick={() => handleEditClick(appointment, index)}
                            className="w-6 h-6 rounded-full bg-white hover:bg-gray-100 flex items-center justify-center"
                            title="Edit"
                          >
                            <FiEdit3 className="w-3 h-3 text-gray-600" />
                          </button>
                          <button
                            onClick={() => handleCancelClick(appointment)}
                            className="w-6 h-6 rounded-full bg-white hover:bg-red-100 flex items-center justify-center"
                            title="Cancel"
                          >
                            <FiTrash2 className="w-3 h-3 text-gray-600" />
                          </button>
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* ===== Financial table ===== */}
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-600 uppercase text-xs tracking-wider">
                  <tr>
                    <th className="px-3 py-2 text-left min-w-[280px]">Line</th>
                    <th className="px-2 py-2 text-center w-12">Qty</th>
                    <th className="px-3 py-2 text-right">MRP (incl. tax)</th>
                    <th className="px-3 py-2 text-right">Discount</th>
                    <th className="px-3 py-2 text-right">Tax</th>
                    <th className="px-3 py-2 text-right">Total</th>
                    <th className="px-3 py-2 text-right w-10"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {/* Main service row — 3 lines */}
                  <tr>
                    <td className="px-3 py-2 align-top">
                      <div className="flex flex-col gap-0.5">
                        {appointment.item_type.toLowerCase() === "grooming" ? (
                          <>
                          {/* Row 1: Pet + Staff (or pet + room for boarding) */}
                            <div className="flex items-center gap-2 flex-wrap leading-tight">
                              <span className="text-gray-500 inline-flex items-center gap-1 font-semibold">
                                <FaDog className="w-3 h-3 text-orange-500" />
                                {appointment.pet_name || "-"}
                                {appointment.pet_breed
                                  ? ` (${appointment.pet_breed})`
                                  : ""}
                                {appointment.pet_size
                                  ? ` • ${appointment.pet_size}`
                                  : ""}
                              </span>
                              <span className="text-gray-300">•</span>
                              {appointment?.employee_name ? (
                                <span className="text-gray-600 inline-flex items-center gap-1">
                                  <FaUserTie className="w-3 h-3 text-purple-500" />
                                  {appointment.employee_name}
                                </span>
                              ) : (
                                <span className="text-red-500 italic inline-flex items-center gap-1">
                                  <FaUserTie className="w-3 h-3 text-red-400" />
                                  Staff not selected
                                </span>
                              )}
                            </div>
                            
                            {/* Row 2: Service name */}
                            <span className="font-semibold text-gray-800 leading-tight">
                              {appointment.service_name || "Service"}
                            </span>
                          </>
                        ) : (
                          <>
                            {/* Row 1: Service name */}
                            <span className="font-semibold text-gray-800 leading-tight">
                              {isBoarding
                                ? getBoardingDisplayText(appointment)
                                : appointment.service_name || "Service"}
                            </span>
                            {/* Row 2: Pet + Staff (or pet + room for boarding) */}
                            <div className="flex items-center gap-2 flex-wrap leading-tight">
                              <span className="text-gray-500 inline-flex items-center gap-1 font-semibold">
                                <FaDog className="w-3 h-3 text-orange-500" />
                                {appointment.pet_name || "-"}
                                {appointment.pet_breed
                                  ? ` (${appointment.pet_breed})`
                                  : ""}
                                {appointment.pet_size
                                  ? ` • ${appointment.pet_size}`
                                  : ""}
                              </span>
                              <span className="text-gray-300">•</span>
                              {isBoarding ? (
                                appointment.room_type_name ||
                                appointment.room_name ||
                                appointment.room_number ? (
                                  <span className="text-gray-600 inline-flex items-center gap-1">
                                    <FiHome className="w-3 h-3 text-indigo-500" />
                                    {appointment.room_type_name ||
                                      appointment.room_name ||
                                      "Room"}
                                    {appointment.room_number
                                      ? ` — ${appointment.room_number}`
                                      : ""}
                                  </span>
                                ) : (
                                  <span className="text-red-500 italic inline-flex items-center gap-1">
                                    <FiHome className="w-3 h-3 text-red-400" />
                                    Room not selected
                                  </span>
                                )
                              ) : appointment.employee_name ? (
                                <span className="text-gray-600 inline-flex items-center gap-1">
                                  <FaUserTie className="w-3 h-3 text-purple-500" />
                                  {appointment.employee_name}
                                </span>
                              ) : (
                                <span className="text-red-500 italic inline-flex items-center gap-1">
                                  <FaUserTie className="w-3 h-3 text-red-400" />
                                  Staff not selected
                                </span>
                              )}
                            </div>
                          </>
                        )}
                        {/* Row 3: Date/time/duration (or In → Out for boarding) */}
                        {isBoarding ? (
                          appointment.start_date && appointment.end_date ? (
                            <span className="text-gray-600 inline-flex items-center gap-1 leading-tight">
                              <FiCalendar className="w-3 h-3 text-blue-500" />
                              In:{" "}
                              {safeFormatDate(
                                appointment.start_date,
                              )?.toLocaleDateString("en-IN", {
                                day: "2-digit",
                                month: "short",
                              })}
                              {" "}→ Out:{" "}
                              {safeFormatDate(
                                appointment.end_date,
                              )?.toLocaleDateString("en-IN", {
                                day: "2-digit",
                                month: "short",
                              })}
                              {" "}• {nights} night{nights > 1 ? "s" : ""}
                            </span>
                          ) : (
                            <span className="text-red-500 italic inline-flex items-center gap-1 leading-tight">
                              <FiCalendar className="w-3 h-3 text-red-400" />
                              Dates not selected
                            </span>
                          )
                        ) : appointmentDate ? (
                          <span className="text-gray-600 inline-flex items-center gap-1 leading-tight">
                            <FiCalendar className="w-3 h-3 text-blue-500" />
                            {appointmentDate.toLocaleDateString("en-IN", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            })}
                            {appointment.start_time
                              ? ` • ${appointment.start_time}`
                              : ""}
                            {appointment.duration
                              ? ` • ${appointment.duration}`
                              : ""}
                          </span>
                        ) : (
                          <span className="text-red-500 italic inline-flex items-center gap-1 leading-tight">
                            <FiCalendar className="w-3 h-3 text-red-400" />
                            Date not selected
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-2 py-2 text-center align-top text-sm">{isBoarding ? 1 : qty}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap align-top">
                      ₹{mrpTotal.toFixed(2)}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap text-red-600 align-top">
                      {discountTotal > 0
                        ? `− ₹${discountTotal.toFixed(2)}`
                        : "—"}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap text-gray-600 align-top">
                      ₹{taxTotal.toFixed(2)}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap text-green-700 font-semibold align-top">
                      ₹{grandTotal.toFixed(2)}
                    </td>
                    <td className="px-3 py-2 align-top"></td>
                  </tr>

                  {/* Add-ons — untouched logic */}
                  {appointment.addons?.map((addon, addonIndex) => {
                    const amt = Number(addon.addon_price || addon.price || 0);
                    const addonQty = Number(addon.quantity || 1);
                    return (
                      <tr
                        key={`apt-${index}-addon-${addonIndex}`}
                        className="bg-green-50/40"
                      >
                        <td className="px-3 py-1.5 pl-6 text-[13px]">
                          <span className="inline-flex items-center gap-1">
                            <FiCheck className="w-3 h-3 text-green-600" />
                            <span className="text-green-700 text-[10px] font-semibold uppercase mr-1">
                              Add-on
                            </span>
                            {addon.addon_name ||
                              addon.name ||
                              `Addon ${addonIndex + 1}`}
                          </span>
                        </td>
                        <td className="px-2 py-1.5 text-center">
                          <div className="inline-flex items-center gap-1">
                            {addonQty > 1 && (
                              <>
                                {!isOrderClosed && !cancelledAppointment && (
                                  <button
                                    onClick={() =>
                                      onDecrementAddonQuantity(
                                        index,
                                        addonIndex,
                                      )
                                    }
                                    className="text-blue-500 hover:text-blue-700"
                                    title="Decrease quantity"
                                  >
                                    <FiMinus className="w-3 h-3" />
                                  </button>
                                )}
                              </>
                            )}
                            <span className="w-5 text-center text-[12px]">
                              {addonQty}
                            </span>
                          </div>
                        </td>
                        <td className="px-3 py-1.5 text-right">
                          ₹{(amt * addonQty).toFixed(2)}
                        </td>
                        <td className="px-3 py-1.5 text-right text-red-600">—</td>
                        <td className="px-3 py-1.5 text-right text-gray-600">—</td>
                        <td className="px-3 py-1.5 text-right text-green-700 font-semibold">
                          ₹{(amt * addonQty).toFixed(2)}
                        </td>
                        <td className="px-3 py-1.5 text-right">
                          {!isOrderClosed && !cancelledAppointment && (
                            <button
                              onClick={() =>
                                onDeleteAppointmentAddon(index, addonIndex)
                              }
                              className="text-red-500 hover:text-red-700"
                              title="Remove addon"
                            >
                              <FiTrash2 className="w-3 h-3 text-gray-600" />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}

                  {/* Products — untouched logic */}
                  {appointment.products?.map((product, productIndex) => {
                    const mrpPrice = Number(
                      product.mrp_price || product.sale_price || 0,
                    );
                    const effectivePrice = Number(product.unit_price || 0);
                    const gstAmount = Number(
                      product.gst || product.gstAmount || 0,
                    );
                    const quantity = Number(product.quantity || 1);
                    const totalEffective = effectivePrice * quantity;
                    const totalGst = gstAmount * quantity;
                    const mrpTotalP = (mrpPrice || effectivePrice);
                    const discountTotalP = (mrpPrice - effectivePrice) * quantity;
                    const grandTotalP =
                      (mrpPrice || effectivePrice) * quantity;
                    return (
                      <tr
                        key={`apt-${index}-prod-${productIndex}`}
                        className="bg-blue-50/40"
                      >
                        <td className="px-3 py-1.5 pl-6 text-[13px]">
                          <span className="inline-flex items-center gap-1">
                            <MdShoppingCart className="w-3 h-3 text-blue-600" />
                            <span className="text-blue-700 text-[10px] font-semibold uppercase mr-1">
                              Product
                            </span>
                            {product.product_name || product.name}
                            {product.gst_slab > 0 && (
                              <span className="italic text-[10px] text-gray-500">
                                (gst {product.gst_slab}%)
                              </span>
                            )}
                          </span>
                        </td>
                        <td className="px-2 py-1.5 text-center">
                          <div className="inline-flex items-center gap-1">
                            {!isOrderClosed && !cancelledAppointment && (
                              <>
                                {quantity > 1 && (
                                  <button
                                    onClick={() =>
                                      onDecrementProductQuantity(
                                        index,
                                        productIndex,
                                      )
                                    }
                                    className="text-black-500 hover:text-black-700"
                                  >
                                    <FiMinus className="w-3 h-3" />
                                  </button>
                                )}
                                {quantity === 1 && (
                                  <button
                                    onClick={() =>
                                      onDeleteAppointmentProduct(
                                        index,
                                        product.product_id || product.id,
                                      )
                                    }
                                    className="text-black-500 hover:text-black-700"
                                  >
                                    <FiTrash2 className="w-3 h-3" />
                                  </button>
                                )}
                              </>
                            )}
                            <span className="w-5 text-center text-[12px]">
                              {quantity}
                            </span>
                            {!isOrderClosed && !cancelledAppointment && (
                              <button
                                onClick={() =>
                                  onIncrementProductQuantity(
                                    index,
                                    productIndex,
                                  )
                                }
                                className="text-black-500 hover:text-black-700"
                              >
                                <FiPlus className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-1.5 text-right">
                          ₹{mrpTotalP.toFixed(2)}
                        </td>
                        <td className="px-3 py-1.5 text-right text-red-600">
                          {discountTotalP > 0 ? `− ₹${discountTotalP.toFixed(2)}` : "—"}
                        </td>
                        <td className="px-3 py-1.5 text-right text-gray-600">
                          ₹{totalGst.toFixed(2)}
                        </td>
                        <td className="px-3 py-1.5 text-right text-green-700 font-semibold">
                          ₹{totalEffective.toFixed(2)}
                        </td>
                        <td className="px-3 py-1.5 text-right">
                          {!isOrderClosed && !cancelledAppointment && (
                            <button
                              onClick={() =>
                                onDeleteAppointmentProduct(
                                  index,
                                  product.product_id || product.id,
                                )
                              }
                              className="text-gray-600 hover:text-red-600"
                              title="Remove product"
                            >
                              <FiTrash2 className="w-3 h-3" />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}

                  {/* Other charges — untouched logic */}
                  {appointment.otherCharges?.map((charge, chargeIndex) => {
                    const amt = Number(charge.amount || 0);
                    return (
                      <tr
                        key={`apt-${index}-charge-${chargeIndex}`}
                        className="bg-purple-50/40"
                      >
                        <td className="px-3 py-1.5 pl-6 text-[13px]">
                          <span className="inline-flex items-center gap-1">
                            <FaInfoCircle className="w-3 h-3 text-purple-600" />
                            <span className="text-purple-700 text-[10px] font-semibold uppercase mr-1">
                              Charge
                            </span>
                            {charge.description || charge.name}
                          </span>
                        </td>
                        <td className="px-2 py-1.5 text-center text-[12px]">1</td>
                        <td className="px-3 py-1.5 text-right">
                          ₹{amt.toFixed(2)}
                        </td>
                        <td className="px-3 py-1.5 text-right text-red-600">—</td>
                        <td className="px-3 py-1.5 text-right text-gray-600">—</td>
                        <td className="px-3 py-1.5 text-right text-green-700 font-semibold">
                          ₹{amt.toFixed(2)}
                        </td>
                        <td className="px-3 py-1.5 text-right">
                          {!isOrderClosed && !cancelledAppointment && (
                            <button
                              onClick={() =>
                                onDeleteAppointmentOtherCharge(
                                  index,
                                  chargeIndex,
                                )
                              }
                              className="text-red-500 hover:text-red-700"
                              title="Remove charge"
                            >
                              <FiTrash2 className="w-3 h-3" />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* ===== mode === "update" editable form — VERBATIM, untouched ===== */}
              {mode === "update" && appointmentEditEnable && (
                <div className="border-t border-dashed border-gray-200 p-4 space-y-4">
                  {isBoarding ? (
                    <>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="flex items-center gap-1 text-sm font-medium text-gray-700 mb-1">
                            <FiHome className="w-4 h-4 text-indigo-600" />
                            Room Type
                          </label>
                          <select
                            value={appointment.room_type_id || ""}
                            onChange={(e) => {
                              const roomTypeId = e.target.value;
                              onAppointmentFieldChange(
                                index,
                                "room_type_id",
                                roomTypeId,
                              );
                              onAppointmentFieldChange(
                                index,
                                "room_number",
                                "",
                              );
                              onAppointmentFieldChange(index, "room_id", "");
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                          >
                            <option value="">Select room type</option>
                            {filteredRoomTypes && filteredRoomTypes.length > 0 ? (
                              filteredRoomTypes.map((roomType) => (
                                <option key={roomType._id} value={roomType._id}>
                                  {roomType.room_name ||
                                    roomType.name ||
                                    `Room Type ${roomType._id}`}
                                </option>
                              ))
                            ) : (
                              <option disabled>
                                No suitable rooms available for{" "}
                                {getPetTypeName(appointment.pet_type)}
                              </option>
                            )}
                          </select>
                          {filteredRoomTypes.length === 0 &&
                            appointment.pet_type && (
                              <p className="text-xs text-red-500 mt-1">
                                No rooms available for{" "}
                                {getPetTypeName(appointment.pet_type)} pets
                              </p>
                            )}
                        </div>

                        <div>
                          <label className="flex items-center gap-1 text-sm font-medium text-gray-700 mb-1">
                            <FiHome className="w-4 h-4 text-indigo-600" />
                            Room Number
                          </label>
                          <select
                            value={appointment.room_number || ""}
                            onChange={(e) =>
                              onAppointmentFieldChange(
                                index,
                                "room_number",
                                e.target.value,
                              )
                            }
                            disabled={!appointment.room_type_id}
                            className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm ${
                              !appointment.room_type_id
                                ? "bg-gray-100 cursor-not-allowed"
                                : ""
                            }`}
                          >
                            <option value="">
                              {appointment.room_type_id
                                ? "Select room number"
                                : "Select room type first"}
                            </option>
                            {(appointment.room_type_id &&
                              roomTypes
                                .find(
                                  (room) =>
                                    room._id === appointment.room_type_id,
                                )
                                ?.room_numbers?.map((bed) => (
                                  <option key={bed._id} value={bed.room_number}>
                                    {bed.room_number}
                                  </option>
                                ))) ||
                              []}
                          </select>
                          {!appointment.room_type_id && (
                            <p className="text-xs text-gray-500 mt-1">
                              Please select a room type first
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="mt-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <MdCurrencyRupee className="w-5 h-5 text-blue-600" />
                            <h4 className="text-sm font-semibold text-blue-800">
                              Price Calculation
                            </h4>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">
                              Price per night:
                            </span>
                            <span className="font-medium">
                              ₹
                              {Number(appointment.unit_price || 0).toFixed(2)}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">
                              Total nights ({nights}):
                            </span>
                            <span className="font-medium">
                              ₹
                              {(
                                Number(appointment.unit_price || 0) * nights
                              ).toFixed(2)}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">GST:</span>
                            <span className="font-medium">
                              ₹{Number(appointment.gst || 0).toFixed(2)}
                            </span>
                          </div>
                          <div className="flex justify-between text-lg font-bold border-t border-blue-300 pt-2">
                            <span>Total Amount:</span>
                            <span className="text-green-600">
                              ₹
                              {Number(appointment.total_price || 0).toFixed(2)}
                            </span>
                          </div>
                          {isCalculatingPrice && (
                            <div className="flex items-center justify-center gap-2 text-sm text-blue-600 mt-2">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                              Calculating price...
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="flex items-center gap-1 text-sm font-medium text-gray-700 mb-1">
                            <FiCalendar className="w-4 h-4 text-blue-600" />
                            Date
                          </label>
                          <input
                            type="date"
                            lang="en-IN"
                            value={appointment.start_date?.split("T")[0] || ""}
                            onChange={(e) =>
                              onAppointmentFieldChange(
                                index,
                                "start_date",
                                e.target.value,
                              )
                            }
                            min={todayStr}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                          />
                        </div>
                        <div>
                          <label className="flex items-center gap-1 text-sm font-medium text-gray-700 mb-1">
                            <FiClock className="w-4 h-4 text-green-600" />
                            Start Time
                          </label>
                          <select
                            value={appointment.start_time || ""}
                            onChange={(e) =>
                              onAppointmentFieldChange(
                                index,
                                "start_time",
                                e.target.value,
                              )
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                          >
                            <option value="">Select time</option>
                            {timeSlots
                              .filter(
                                (slot) =>
                                  !isPastTimeSlot(
                                    slot,
                                    appointment.start_date,
                                  ),
                              )
                              .map((slot) => (
                                <option key={slot} value={slot}>
                                  {slot}
                                </option>
                              ))}
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="flex items-center gap-1 text-sm font-medium text-gray-700 mb-1">
                          <FiUser className="w-4 h-4 text-purple-600" />
                          Staff
                        </label>
                        <select
                          value={appointment.technician_id || ""}
                          onChange={(e) => {
                            const staffId = e.target.value;
                            const selectedEmployee = employees.find(
                              (emp) =>
                                emp._id === staffId ||
                                emp.id === staffId ||
                                emp.employee_id === staffId,
                            );

                            onAppointmentFieldChange(
                              index,
                              "technician_id",
                              staffId,
                            );

                            const staffName =
                              selectedEmployee?.name ||
                              selectedEmployee?.first_name ||
                              selectedEmployee?.employee_name ||
                              `${selectedEmployee?.first_name || ""} ${
                                selectedEmployee?.last_name || ""
                              }`.trim() ||
                              "Staff";

                            onAppointmentFieldChange(
                              index,
                              "employee_name",
                              staffName,
                            );
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                        >
                          <option value="">Select staff</option>
                          {employees && employees.length > 0 ? (
                            employees.map((employee) => {
                              const staffName =
                                employee.name ||
                                employee.first_name ||
                                employee.employee_name ||
                                employee.full_name ||
                                `${employee.first_name || ""} ${employee.last_name || ""}`.trim() ||
                                "Unknown Staff";

                              return (
                                <option key={employee._id} value={employee._id}>
                                  {staffName}
                                </option>
                              );
                            })
                          ) : (
                            <option disabled>No staff available</option>
                          )}
                        </select>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* Products Section */}
        {products.length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden mt-3">
            <div className="px-6 py-3 bg-gradient-to-r from-green-50 to-emerald-50 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center">
                  <MdShoppingCart className="w-4 h-4 text-white" />
                </div>
                <h3 className="text-sm font-semibold text-gray-900">
                  Products
                </h3>
                <span className="ml-auto px-2 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                  {products.length} {products.length === 1 ? "item" : "items"}
                </span>
              </div>
            </div>

            <div className="p-4 space-y-2">
              {products.map((product, idx) => {
                const qty = parseFloat(product.quantity || 1);
                
                // Get the amount without tax (this is the base price)
                const amountWithoutTax = Number(product.amount_without_tax || 0);
                const gstAmount = Number(product.gst || product.tax_amount || 0);
                const totalWithTax = amountWithoutTax + gstAmount;
                
                // Calculate per-unit values
                const unitGst = gstAmount / qty;
                const unitPriceWithTax = totalWithTax / qty;
                
                // Get MRP and calculate discount
                const mrp = Number(product.mrp || 0);
                const hasDiscount = mrp > 0 && mrp !== unitPriceWithTax && unitPriceWithTax < mrp;
                const savingsPerUnit = mrp - unitPriceWithTax;
                
                const isTooltipVisible = activeTooltipIndex === idx;
                
                return (
                  <div
                    key={`${product.service_id}-${idx}`}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100 hover:bg-gray-100 transition-colors group"
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <MdShoppingCart className="w-4 h-4 text-green-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 text-xs">
                          {product.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          SKU: {product.sku_number}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 ml-2">
                      <div className="text-center">
                        <p className="text-xs text-gray-500 mb-0.5">Quantity</p>
                        <span className="text-xs font-semibold text-gray-900">{qty}</span>
                      </div>

                      <div className="text-center min-w-[90px]">
                        <p className="text-xs text-gray-500 mb-0.5">Unit Price</p>
                        {hasDiscount ? (
                          <div className="flex flex-col items-center">
                            <div className="flex items-center gap-1">
                              <span className="text-gray-400 line-through text-xs">
                                ₹{mrp.toFixed(2)}
                              </span>
                              <div className="relative inline-block">
                                <FaInfoCircle
                                  className="text-gray-500 text-xs cursor-help hover:text-gray-700"
                                  onMouseEnter={() => setActiveTooltipIndex(idx)}
                                  onMouseLeave={() => setActiveTooltipIndex(null)}
                                />
                                {isTooltipVisible && (
                                  <div className="absolute z-50 bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-auto whitespace-nowrap bg-gray-800 text-white text-xs rounded-lg py-1.5 px-3 shadow-lg">
                                    <div className="flex flex-col gap-1">
                                      <div className="flex items-center gap-2">
                                        <span className="text-gray-300">Discount:</span>
                                        <span className="text-green-400 font-semibold">
                                          ₹{savingsPerUnit.toFixed(2)}/unit
                                        </span>
                                      </div>
                                      {product.discount_amount > 0 && (
                                        <div className="flex items-center gap-2 border-t border-gray-600 mt-1 pt-1">
                                          <span className="text-gray-300">Extra Discount:</span>
                                          <span className="text-blue-400 font-semibold">
                                            ₹{product.discount_amount.toFixed(2)}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                    <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2">
                                      <div className="border-4 border-transparent border-t-gray-800"></div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                            <span className="text-xs font-medium text-green-600">
                              ₹{unitPriceWithTax.toFixed(2)}
                            </span>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center">
                            <span className="text-xs font-medium text-gray-800">
                              ₹{unitPriceWithTax.toFixed(2)}
                            </span>
                          </div>
                        )}
                        {gstAmount > 0 && (
                          <p className="text-[10px] text-gray-400">+ GST: ₹{unitGst.toFixed(2)}</p>
                        )}
                      </div>

                      <div className="text-center">
                        <p className="text-xs text-gray-500 mb-0.5">Total</p>
                        <p className="text-sm font-bold text-green-600">
                          ₹{amountWithoutTax.toFixed(2)}
                        </p>
                        {gstAmount > 0 && (
                          <p className="text-[10px] text-gray-500">+ GST: ₹{gstAmount.toFixed(2)}</p>
                        )}
                      </div>

                    {!isOrderClosed && (
                      <button
                        onClick={() => onDeleteProduct(idx)}
                        className="w-5 h-5 rounded-full bg-gray-200 group-hover:bg-red-200 flex items-center justify-center transition-colors flex-shrink-0"
                        title="Delete"
                      >
                        <FiTrash2 className="w-3 h-3 text-gray-600 group-hover:text-red-600" />
                      </button>
                    )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Catalog Drawer */}
      {catalogOpenForIndex !== null && (
        <div className="fixed inset-0 z-[100]">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={closeCatalog}
          />
          <div className="absolute right-0 top-0 h-full w-full bg-white shadow-xl transform transition-transform duration-300 ease-out flex flex-col">
            <div className="flex items-center p-4 border-b bg-gradient-to-r from-blue-50 to-indigo-50 shadow-sm">
              <button
                onClick={closeCatalog}
                className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-white/70 transition-colors duration-200"
              >
                <HiArrowSmLeft className="w-6 h-6 text-gray-700" />
              </button>
              <div className="text-lg font-semibold ml-3">Add Products</div>
            </div>

            <div className="px-4 flex-1 overflow-y-auto">
              {catalogLoading ? (
                <div className="p-4 text-sm text-gray-500">
                  Loading products...
                </div>
              ) : catalogError ? (
                <div className="p-4 text-sm text-red-500">{catalogError}</div>
              ) : (
                <div className="space-y-3 py-3">
                  {catalogProducts.map((prod, i) => (
                    <div
                      key={`catalog-${prod.id}-${i}`}
                      className="flex items-center justify-between p-3 bg-white rounded-lg border shadow-sm hover:shadow-md transition"
                    >
                      <div className="w-14 h-14 rounded-md overflow-hidden bg-gray-100 border mr-3">
                        <img
                          src={prod.image || prod.thumbnail || "/no-image.png"}
                          alt={prod.name}
                          className="w-full h-full object-cover"
                        />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-800 truncate">
                          {prod.name}
                        </div>
                        <div className="text-xs text-gray-500">
                          ₹{Number(prod.price).toFixed(2)}
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() =>
                            changeCatalogQty(
                              prod.id,
                              Math.max(0, (catalogCart[prod.id] || 0) - 1),
                            )
                          }
                          className="w-8 h-8 flex items-center justify-center border rounded-md hover:bg-gray-100"
                        >
                          -
                        </button>

                        <input
                          type="number"
                          min={0}
                          value={catalogCart[prod.id] ?? 0}
                          onChange={(e) =>
                            changeCatalogQty(prod.id, Number(e.target.value))
                          }
                          className="w-14 text-center border rounded p-1 text-sm"
                        />

                        <button
                          type="button"
                          onClick={() =>
                            changeCatalogQty(
                              prod.id,
                              (catalogCart[prod.id] || 0) + 1,
                            )
                          }
                          className="w-8 h-8 flex items-center justify-center border rounded-md hover:bg-gray-100"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="p-4 border-t flex justify-end gap-2 bg-gray-50">
              <button
                onClick={closeCatalog}
                className="px-4 py-2 rounded-md bg-white border"
              >
                Cancel
              </button>
              <button
                onClick={() => onCatalogAddToCart(catalogOpenForIndex)}
                className="px-4 py-2 rounded-md bg-emerald-600 text-white hover:bg-emerald-700"
              >
                Add to Cart
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PetServiceCategoryDrawer */}
      {showPetServiceDrawer && (
        <PetServiceCategoryDrawer
          isOpen={showPetServiceDrawer}
          onClose={handleDrawerClose}
          onUpdate={handleSavedAppointment}
          clientId={clientData?._id}
          clientName={`${clientData?.firstname || ""} ${clientData?.lastname || ""}`.trim()}
          clientNumber={clientData?.phone || ""}
          itemData={itemData}
          orderId={orderId}
          fromCalendar={false}
          editMode={drawerEditMode}
          onRefreshData={onRefreshData}
          isServiceEditMode={drawerEditMode}
          isPetDetails={false}
          drawerWidth="w-full"
        />
      )}
    </>
  );
};

export default PetServiceList;
